import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Users, LogOut, Loader2, ShieldCheck, Activity, CreditCard, FlaskConical,
  RotateCcw, ScrollText, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  trial:   { bg: "hsl(var(--haruki-leaf) / 0.15)", fg: "hsl(var(--haruki-leaf-deep))", label: "Trial" },
  paid:    { bg: "hsl(var(--haruki-teal) / 0.15)", fg: "hsl(var(--haruki-teal))", label: "Paid" },
  expired: { bg: "hsl(var(--haruki-rust) / 0.15)", fg: "hsl(var(--haruki-rust))", label: "Expired" },
};

export default function PlatformUsers() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isDemo = user?.role === "demo_admin";
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flip, setFlip] = useState(null); // user being status-flipped (opens confirm modal)
  const [payRef, setPayRef] = useState("");
  const [flipSubmitting, setFlipSubmitting] = useState(false);
  const [demoAdminEmail, setDemoAdminEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [o, u, a] = await Promise.all([
        api.get("/platform/overview"),
        api.get("/platform/users"),
        api.get("/platform/audit"),
      ]);
      setOverview(o.data);
      setRows(u.data.users);
      setAudit(a.data.events);
    } catch (e) {
      toast.error("Could not load dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const submitFlip = async () => {
    const next = flip.account_status === "paid" ? "trial" : "paid";
    try {
      await api.post(`/platform/users/${flip.user_id}/status`, {
        status: next,
        payment_reference: next === "paid" ? payRef || null : null,
      });
      toast.success(`Status changed to ${next}.`);
      setFlip(null); setPayRef("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change status.");
    }
  };

  const resetDemo = async () => {
    try {
      await api.post("/platform/demo/reset");
      toast.success("Demo data restored to the seeded set.");
      load();
    } catch { toast.error("Reset failed."); }
  };

  const manageDemoAdmin = async (action) => {
    if (!demoAdminEmail.trim()) return;
    try {
      await api.post("/platform/demo-admins", { email: demoAdminEmail.trim(), action });
      toast.success(`demo_admin ${action}ed for ${demoAdminEmail.trim()}`);
      setDemoAdminEmail("");
    } catch (e) { toast.error("Could not update demo admin."); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-teal))" }}>
              <Users className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight">Haruki Health</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>
                Admin · Users
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isDemo && (
              <Link to="/platform" className="text-xs text-muted-foreground hover:text-foreground" data-testid="admin-back-platform">
                <ArrowLeft className="mr-1 inline h-3 w-3" />Platform overview
              </Link>
            )}
            <span className="hidden text-xs text-muted-foreground sm:inline">{user?.email}</span>
            <Button data-testid="admin-logout-button" variant="ghost" size="sm"
                    onClick={async () => { await logout(); nav("/"); }}>
              <LogOut className="mr-2 h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 lg:px-10">
        {isDemo && (
          <div data-testid="demo-mode-banner"
               className="mb-6 flex items-center justify-between rounded-md border px-4 py-2.5"
               style={{ borderColor: "hsl(var(--haruki-teal) / 0.4)", background: "hsl(var(--haruki-teal) / 0.06)" }}>
            <div className="flex items-center gap-2 text-xs" style={{ color: "hsl(var(--haruki-teal))" }}>
              <FlaskConical className="h-3.5 w-3.5" />
              <span className="font-semibold">Demo environment</span>
              <span className="text-muted-foreground">— synthetic data for walkthroughs</span>
            </div>
            <Button data-testid="demo-reset-button" variant="outline" size="sm" onClick={resetDemo}>
              <RotateCcw className="mr-1.5 h-3 w-3" /> Reset demo data
            </Button>
          </div>
        )}

        <h1 className="font-heading text-3xl font-bold">User accounts</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Signups, account status, and activity{isDemo ? " (demo dataset)" : ""}.
        </p>

        {loading ? (
          <div className="mt-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {overview && (
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat icon={Users} label="Total users" value={overview.total_users} test="admin-stat-total" />
                <Stat icon={ShieldCheck} label="Active trials" value={overview.active_trials} test="admin-stat-trials" />
                <Stat icon={CreditCard} label="Paid users" value={overview.paid_users} test="admin-stat-paid" />
                <Stat icon={Activity} label="Scenarios completed" value={overview.scenarios_completed} test="admin-stat-scenarios" />
              </div>
            )}

            <Card className="mt-8">
              <CardHeader><CardTitle className="font-heading text-base">Users ({rows.length})</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Signed up</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Trial ends</th>
                        <th className="px-4 py-3">Last activity</th>
                        <th className="px-4 py-3 text-right">Done</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const st = STATUS_STYLE[r.account_status] || { bg: "hsl(var(--muted))", fg: "inherit", label: r.account_status };
                        return (
                          <tr key={r.user_id} data-testid={`admin-user-row-${r.user_id}`} className="border-b border-border last:border-0">
                            <td className="px-4 py-3 font-medium">{r.name || "—"}</td>
                            <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">{r.signup_at?.slice(0, 10) || "—"}</td>
                            <td className="px-4 py-3">
                              <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: st.bg, color: st.fg }}>
                                {st.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">{r.trial_ends_at?.slice(0, 10) || "—"}</td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">{r.last_activity_at?.slice(0, 10) || "—"}</td>
                            <td className="px-4 py-3 text-right tabular-nums">{r.scenarios_completed ?? 0}</td>
                            <td className="px-4 py-3 text-right">
                              {(r.account_status === "trial" || r.account_status === "expired" || r.account_status === "paid") && (
                                <Button data-testid={`admin-flip-${r.user_id}`} variant="outline" size="sm"
                                        onClick={() => { setFlip(r); setPayRef(r.payment_reference || ""); }}>
                                  {r.account_status === "paid" ? "→ Trial" : "→ Paid"}
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {rows.length === 0 && (
                        <tr><td colSpan="8" className="px-4 py-8 text-center text-sm text-muted-foreground">No users.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Dialog open={!!flip} onOpenChange={(open) => { if (!open) { setFlip(null); setPayRef(""); } }}>
              <DialogContent data-testid="admin-flip-panel">
                <DialogHeader>
                  <DialogTitle className="font-heading text-base">
                    Change {flip?.name || flip?.email} from {flip?.account_status} → {flip?.account_status === "paid" ? "trial" : "paid"}
                  </DialogTitle>
                  <DialogDescription>
                    {flip?.account_status === "paid"
                      ? "This moves the user's agency back to a trial. Their access will follow the trial window."
                      : "This marks the user's agency as paid and promotes the trial owner to the Agency Dashboard."}
                  </DialogDescription>
                </DialogHeader>
                {flip && flip.account_status !== "paid" && (
                  <div>
                    <label className="text-xs text-muted-foreground">Stripe customer / payment reference (optional)</label>
                    <Input data-testid="admin-flip-payref" value={payRef} onChange={(e) => setPayRef(e.target.value)}
                           placeholder="cus_… or invoice/PO number" className="mt-1" />
                  </div>
                )}
                <DialogFooter>
                  <Button data-testid="admin-flip-cancel" variant="ghost"
                          onClick={() => { setFlip(null); setPayRef(""); }}>Cancel</Button>
                  <Button data-testid="admin-flip-confirm" onClick={submitFlip} disabled={flipSubmitting}
                          style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}>
                    {flipSubmitting ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Saving…</> : "Confirm"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="font-heading text-base">
                  <ScrollText className="mr-2 inline h-4 w-4" />Audit log
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0" data-testid="admin-audit-log">
                <table className="w-full text-sm">
                  <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">When</th>
                      <th className="px-4 py-3">Who</th>
                      <th className="px-4 py-3">Action</th>
                      <th className="px-4 py-3">Target</th>
                      <th className="px-4 py-3">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((e) => (
                      <tr key={e.event_id} className="border-b border-border last:border-0">
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{e.at?.slice(0, 16).replace("T", " ")}</td>
                        <td className="px-4 py-2.5 text-xs">{e.actor_email}</td>
                        <td className="px-4 py-2.5 text-xs font-medium">{e.action?.replace(/_/g, " ")}</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{e.target}</td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">
                          {e.before?.access_status || e.before?.account_status || ""}
                          {(e.before?.access_status || e.before?.account_status) ? " → " : ""}
                          {e.after?.access_status || e.after?.account_status || e.after?.change || ""}
                          {e.meta?.payment_reference ? ` · ${e.meta.payment_reference}` : ""}
                        </td>
                      </tr>
                    ))}
                    {audit.length === 0 && (
                      <tr><td colSpan="5" className="px-4 py-6 text-center text-xs text-muted-foreground">No audit events yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {!isDemo && (
              <Card className="mt-8">
                <CardHeader><CardTitle className="font-heading text-base">Demo admin access</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Grant demo_admin to any email (they sign in by magic link and see only synthetic data). Reassignable anytime.
                  </p>
                  <div className="flex max-w-md gap-2">
                    <Input data-testid="admin-demoadmin-email" value={demoAdminEmail}
                           onChange={(e) => setDemoAdminEmail(e.target.value)} placeholder="ceo@example.com" />
                    <Button data-testid="admin-demoadmin-grant" size="sm" onClick={() => manageDemoAdmin("grant")}>Grant</Button>
                    <Button data-testid="admin-demoadmin-revoke" size="sm" variant="outline" onClick={() => manageDemoAdmin("revoke")}>Revoke</Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, test }) => (
  <Card data-testid={test}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2">
        <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-teal))", color: "white" }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 font-heading text-2xl font-extrabold tabular-nums">{value}</div>
    </CardContent>
  </Card>
);
